import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { SUBJECTS } from '@/data/flashcards';
import { ALL_QUIZ_QUESTIONS, QUIZ_CONFIG } from '@/data/quizQuestions';
import { ArrowLeft, Clock, CheckCircle2, XCircle, RotateCcw, Trophy, Timer, Play } from 'lucide-react';

const QuizScreen = ({ onBack }) => {
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(QUIZ_CONFIG.timeLimit);
  const [isFinished, setIsFinished] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [quizStarted, setQuizStarted] = useState(false);

  const questions = selectedSubject ? ALL_QUIZ_QUESTIONS[selectedSubject] : [];
  const currentQuestion = questions[currentQuestionIndex];
  const subject = SUBJECTS.find(s => s.id === selectedSubject);

  // Timer
  useEffect(() => {
    if (!quizStarted || isFinished || showResult || !selectedSubject) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleTimeUp();
          return QUIZ_CONFIG.timeLimit;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [quizStarted, isFinished, showResult, selectedSubject, currentQuestionIndex]);

  const handleTimeUp = useCallback(() => {
    if (selectedAnswer === null) {
      // Zeit abgelaufen ohne Antwort
      setAnswers(prev => [...prev, { questionId: currentQuestion.id, selected: -1, correct: currentQuestion.correct, isCorrect: false }]);
      setShowResult(true);
      setTimeout(() => moveToNext(), 2000);
    }
  }, [selectedAnswer, currentQuestion]);

  const handleAnswerSelect = (index) => {
    if (showResult) return;
    setSelectedAnswer(index);
    
    const isCorrect = index === currentQuestion.correct;
    if (isCorrect) {
      setScore(prev => prev + 1);
    }
    
    setAnswers(prev => [...prev, { 
      questionId: currentQuestion.id, 
      selected: index, 
      correct: currentQuestion.correct, 
      isCorrect 
    }]);
    
    setShowResult(true);
    setTimeout(() => moveToNext(), 1500);
  };

  const moveToNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setShowResult(false);
      setTimeLeft(QUIZ_CONFIG.timeLimit);
    } else {
      setIsFinished(true);
    }
  };

  const startQuiz = (subjectId) => {
    setSelectedSubject(subjectId);
    setQuizStarted(true);
    setCurrentQuestionIndex(0);
    setScore(0);
    setAnswers([]);
    setTimeLeft(QUIZ_CONFIG.timeLimit);
    setIsFinished(false);
    setShowResult(false);
    setSelectedAnswer(null);
  };

  const restartQuiz = () => {
    startQuiz(selectedSubject);
  };

  const backToSubjects = () => {
    setSelectedSubject(null);
    setQuizStarted(false);
    setIsFinished(false);
  };

  // Subject Selection Screen
  if (!selectedSubject) {
    return (
      <div className="min-h-screen bg-background pb-24">
        <div className="px-4 pt-6 pb-4">
          <div className="flex items-center gap-3 mb-6">
            <Button variant="ghost" size="icon" onClick={onBack}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h2 className="text-2xl font-display font-bold text-foreground">Quiz</h2>
              <p className="text-muted-foreground text-sm">Multiple Choice mit Zeitlimit</p>
            </div>
          </div>

          <Card className="bg-primary/10 border-primary/20 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Timer className="w-6 h-6 text-primary" />
                <div>
                  <p className="font-semibold text-foreground">Zeitlimit: {QUIZ_CONFIG.timeLimit} Sekunden</p>
                  <p className="text-sm text-muted-foreground">Pro Frage hast du {QUIZ_CONFIG.timeLimit} Sekunden Zeit</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <h3 className="text-lg font-semibold text-foreground mb-4">Wähle ein Fach</h3>
          
          <div className="space-y-3">
            {SUBJECTS.map((subject, index) => (
              <Card
                key={subject.id}
                className="border-border cursor-pointer hover:border-primary/50 transition-all active:scale-[0.98] animate-slide-up"
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => startQuiz(subject.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center text-2xl">
                      {subject.icon}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-foreground">{subject.name}</h4>
                      <p className="text-sm text-muted-foreground">20 Multiple Choice Fragen</p>
                    </div>
                    <Play className="w-5 h-5 text-primary" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Results Screen
  if (isFinished) {
    const percentage = Math.round((score / questions.length) * 100);
    const passed = percentage >= 50;

    return (
      <div className="min-h-screen bg-background flex flex-col">
        <div className="px-4 pt-6 pb-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={backToSubjects}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h2 className="font-semibold text-foreground">Quiz Ergebnis</h2>
              <p className="text-sm text-muted-foreground">{subject?.name}</p>
            </div>
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-4">
          <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-6 animate-scale-in ${
            passed ? 'bg-success/20' : 'bg-destructive/20'
          }`}>
            {passed ? (
              <Trophy className="w-12 h-12 text-success" />
            ) : (
              <XCircle className="w-12 h-12 text-destructive" />
            )}
          </div>

          <h2 className="text-3xl font-display font-bold text-foreground mb-2">
            {percentage}%
          </h2>
          <p className={`text-lg font-medium mb-2 ${passed ? 'text-success' : 'text-destructive'}`}>
            {passed ? 'Bestanden!' : 'Nicht bestanden'}
          </p>
          <p className="text-muted-foreground mb-8">
            {score} von {questions.length} Fragen richtig
          </p>

          <div className="w-full max-w-xs space-y-3">
            <Button onClick={restartQuiz} className="w-full" size="lg">
              <RotateCcw className="w-4 h-4 mr-2" />
              Nochmal versuchen
            </Button>
            <Button variant="outline" onClick={backToSubjects} className="w-full" size="lg">
              Anderes Fach wählen
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Quiz Question Screen
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="px-4 pt-6 pb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={backToSubjects}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h2 className="font-semibold text-foreground">{subject?.icon} {subject?.name}</h2>
              <p className="text-sm text-muted-foreground">
                Frage {currentQuestionIndex + 1} von {questions.length}
              </p>
            </div>
          </div>
          
          {/* Timer */}
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
            timeLeft <= 10 ? 'bg-destructive/20 text-destructive' : 'bg-primary/10 text-primary'
          }`}>
            <Clock className="w-4 h-4" />
            <span className="font-bold tabular-nums">{timeLeft}s</span>
          </div>
        </div>

        {/* Progress */}
        <Progress 
          value={((currentQuestionIndex + 1) / questions.length) * 100} 
          className="h-1.5 bg-muted"
        />
        
        {/* Score */}
        <div className="flex items-center justify-end mt-2">
          <span className="text-sm text-muted-foreground">
            Punkte: <span className="text-success font-semibold">{score}</span>
          </span>
        </div>
      </div>

      {/* Question */}
      <div className="flex-1 px-4 pb-4">
        <Card className="bg-card border-border mb-4">
          <CardContent className="p-5">
            <p className="text-lg text-foreground leading-relaxed">
              {currentQuestion?.question}
            </p>
          </CardContent>
        </Card>

        {/* Answer Options */}
        <div className="space-y-3">
          {currentQuestion?.options.map((option, index) => {
            let buttonClass = 'w-full justify-start text-left h-auto py-4 px-4';
            let variant = 'outline';
            
            if (showResult) {
              if (index === currentQuestion.correct) {
                buttonClass += ' border-success bg-success/10 text-success';
              } else if (index === selectedAnswer && index !== currentQuestion.correct) {
                buttonClass += ' border-destructive bg-destructive/10 text-destructive';
              }
            } else if (selectedAnswer === index) {
              buttonClass += ' border-primary bg-primary/10';
            }

            return (
              <Button
                key={index}
                variant={variant}
                className={buttonClass}
                onClick={() => handleAnswerSelect(index)}
                disabled={showResult}
              >
                <span className="flex items-center gap-3">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                    showResult && index === currentQuestion.correct
                      ? 'bg-success text-success-foreground'
                      : showResult && index === selectedAnswer
                        ? 'bg-destructive text-destructive-foreground'
                        : 'bg-muted text-muted-foreground'
                  }`}>
                    {String.fromCharCode(65 + index)}
                  </span>
                  <span className="flex-1">{option}</span>
                  {showResult && index === currentQuestion.correct && (
                    <CheckCircle2 className="w-5 h-5 text-success" />
                  )}
                  {showResult && index === selectedAnswer && index !== currentQuestion.correct && (
                    <XCircle className="w-5 h-5 text-destructive" />
                  )}
                </span>
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default QuizScreen;